import * as React from 'react';
import { AlertCircle, CheckCircle2, Download, Eye, FileText, RotateCcw, Upload, X } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

// ─── Public types ────────────────────────────────────────────────────────────

/** A generic category option rendered in the (optional) type picker. */
export interface FileUploadCategoryOption {
  /** Human-readable label already translated by the consumer. */
  label: string;
  /** Stable value stamped onto the upload and matched against a category picker. */
  value: string;
}

/** A file rendered in the list — either seeded via `initialFiles` or produced by an upload. */
export interface FileUploadItem {
  /** Optional category code. */
  category?: string;
  /** MIME type of the file. */
  contentType: string;
  /**
   * The source `File` object — present only for a file added THIS browser session (picked or dropped),
   * absent for a seeded/persisted item (from `initialFiles`). A consumer can use it to serve an unsaved
   * file's preview/download straight from memory (e.g. via `URL.createObjectURL`), since the item's `id`
   * may not resolve to anything server-side yet.
   */
  file?: File;
  /** Display name of the file. */
  fileName: string;
  /** Stable persisted identifier. */
  id: string;
  /** Object URL for an image thumbnail (local previews only). */
  previewUrl?: string;
  /** File size in bytes. */
  sizeBytes: number;
  /**
   * `itemVariant: 'detailed'` only: when the file was uploaded. Rendered through
   * `labels.uploadedAtFormat`, since only the consumer knows the locale and the wording.
   */
  uploadedAt?: Date | string;
  /** `itemVariant: 'detailed'` only: who uploaded the file. Omitted when the record does not carry it. */
  uploadedBy?: string;
}

/** Context handed to the consumer-provided `onUploadBatch` implementation. */
export interface FileUploadBatchContext {
  /**
   * Category shared by every file in the batch (fixed / before-mode picked). Undefined when the batch
   * mixes categories (after-mode per-file tagging), since no single value describes the whole batch.
   */
  category?: string;
  /** Abort signal — aborts the whole batch when any of its items is removed mid-upload. */
  signal: AbortSignal;
  /** Report upload progress (0–100) for ONE file in the batch (matched by file identity). */
  onFileProgress: (file: File, percent: number) => void;
}

/** One entry the consumer returns from `onUploadBatch`, per successfully uploaded file. */
export interface FileUploadBatchResult {
  /** The uploaded source file — must be one of the files passed in (matched by identity). */
  file: File;
  /** Persisted document id. */
  id: string;
  /** Resolved category (defaults to the file's gate category when omitted). */
  category?: string;
}

/** Consumer-provided, already-translated strings — the component stays i18n-agnostic. */
export interface FileUploadLabels {
  /** `zoneVariant: 'row'` only: the upload button's label (e.g. "Upload file(s)"). Falls back to `browse`. */
  addRowButton?: string;
  /**
   * `zoneVariant: 'row'` only: muted description under the title (e.g. "Uploaded as an insurance
   * document") — deliberately a SEPARATE field from `dropzone` since this copy is consumer/context
   * specific (an acquisition uploader would read differently than an insurance one).
   */
  addRowDescription?: string;
  /**
   * `zoneVariant: 'row'` only: bold row title (e.g. "Add document"). Omit to render no title, for a
   * layout where the row is only the button and its description.
   */
  addRowTitle?: string;
  /** Label for the "browse" button that opens the native file dialog. */
  browse: string;
  /** Renders the list header count, e.g. `2 of 10`. Omit to render no count. */
  countOf?: (shown: number, max: number) => string;
  /** Accessible label for the per-card download (save-to-disk) button. */
  download: string;
  /** Primary drop-zone prompt (e.g. "Drag & drop files here"). Not shown when `zoneVariant` is `'row'`. */
  dropzone: string;
  /** Message shown when the list is empty. */
  empty: string;
  /** Error: selected file's type is not permitted by `accept`. */
  errorBadType: string;
  /** Error: no file was selected. */
  errorNoFile: string;
  /**
   * Before-mode: why the add-files control is inert until a type is picked (e.g. "Choose a document
   * type first"). Doubles as the error on a file that reaches the list untyped, which the gate makes
   * unreachable through the UI — so word it as the instruction, not the complaint.
   */
  errorNoType: string;
  /** Error: selected file exceeds `maxSizeBytes`. */
  errorTooLarge: string;
  /** Error: adding the file would exceed `maxFiles`. */
  errorTooMany: string;
  /** Generic error shown when an upload rejects. */
  errorUpload: string;
  /** Title rendered above the file list. Omit to render no title. */
  listTitle?: string;
  /** Accessible label / tooltip for the per-card open action (prefixed to the file name). */
  open: string;
  /** Accessible label for the per-card remove button. */
  remove: string;
  /** Accessible label for the per-card retry button. */
  retry: string;
  /** Optional chip shown on a staged (deferred-mode) item, e.g. "Will upload when saved". Omit to hide it. */
  staged?: string;
  /**
   * Label above the category picker (before-mode) and status chip while uploading. Omit to render no
   * visible label; the picker then takes its accessible name from `typePlaceholder`.
   */
  typeLabel?: string;
  /**
   * `itemVariant: 'detailed'` only: renders an item's `uploadedAt` (e.g. "20 Feb 2026", "just now").
   * Omit to leave the date out of the meta line — the component never formats a date itself, because
   * the locale and the absolute/relative choice belong to the consumer.
   */
  uploadedAtFormat?: (value: Date | string) => string;
  /** Placeholder shown in the category picker before a selection. */
  typePlaceholder: string;
  /** Status text shown while an upload is in flight. */
  uploading: string;
}

export interface FileUploadProps {
  /** Comma-separated allowed formats (e.g. `.pdf,.jpg,.jpeg,.png`, `image/*`). */
  accept?: string;
  /** Type-picker options; when provided, the tagging UI is enabled. */
  categories?: FileUploadCategoryOption[];
  /** Disables the whole control. */
  disabled?: boolean;
  /** When set, no picker is rendered and this category is stamped on every upload. */
  fixedCategory?: string;
  /** Persisted files used to seed the list (reconciled by id). */
  initialFiles?: FileUploadItem[];
  /** Consumer-provided translated strings. */
  labels: FileUploadLabels;
  /** Maximum number of files allowed in total (default `1`). */
  maxFiles?: number;
  /**
   * Replace mode: the add control stays usable when the list already holds `maxFiles` uploaded files, and
   * a newly uploaded file takes the place of the existing ones — they leave the list once it succeeds,
   * without `onRemove` (the consumer's upload is expected to replace them server-side). `maxFiles` still
   * caps how many files one drop may add. Use it for a one-document slot whose upload overwrites the
   * previous file (e.g. a contract version's quotation PDF).
   */
  replaceOnUpload?: boolean;
  /** Per-file size limit in bytes. */
  maxSizeBytes?: number;
  /**
   * Upload timing. `immediate` (default) uploads valid files (via `onUploadBatch`) as soon as they are added. `deferred`
   * validates + stages valid files locally (no network) until an imperative {@link FileUploadHandle.flush}
   * uploads them — used when the upload target id only exists after a parent save.
   */
  mode?: 'deferred' | 'immediate';
  /** Called to force-download (save-to-disk) a done item; renders a per-card download button when set. */
  onDownload?: (item: FileUploadItem) => void;
  /** Called when a done item's content region (thumbnail + name + size) is activated — e.g. to open it. */
  onOpen?: (item: FileUploadItem) => void;
  /**
   * Called with the item to remove (the component owns no network). Resolve `false` to CANCEL the
   * removal — what a consumer that asks the user to confirm returns when they decline; the card then
   * stays exactly as it was, with no error. Any other resolution removes it, and a rejection leaves
   * the card in an error state.
   */
  onRemove?: (item: FileUploadItem) => boolean | void | Promise<boolean | void>;
  /**
   * Called with how many items the list currently shows — persisted, staged and in-flight alike. For a
   * consumer rendering its own count outside the component, this is the number to render: a count
   * taken from the persisted files alone would read "no files" while a staged one is on screen.
   */
  onItemCountChange?: (count: number) => void;
  /** Called when a done item's category is changed via the per-card picker. */
  onRetag?: (item: FileUploadItem, category: string) => void | Promise<void>;
  /**
   * Performs the upload. Every file dropped in ONE action is handed over together so the consumer can upload
   * them with a single shared setup (e.g. one SAS request for N files + one batched record). Report per-file
   * progress via `ctx.onFileProgress`; resolve with one {@link FileUploadBatchResult} per successful file
   * (any file not returned is marked errored). With no handler wired, dropped files are just listed.
   */
  onUploadBatch?: (files: File[], ctx: FileUploadBatchContext) => Promise<FileUploadBatchResult[]>;
  /**
   * Read-only list mode. Unlike `disabled` (which keeps the greyed-out add zone on screen), `readOnly`
   * removes the add affordance (dropzone / row) and the type picker ENTIRELY, and hides every per-card
   * remove control — leaving only the list of files, each still openable and downloadable. Use it for a
   * frozen owner (an archived vehicle, a closed / cancelled work order) where files stay readable but the
   * list can no longer be managed.
   */
  readOnly?: boolean;
  /**
   * A file card's shape. `compact` (default) is one line: name, then size on the right. `detailed`
   * stacks a meta line under the name — extension, size, uploader and upload date, whichever of them
   * the item carries — and shows the item's category as a read-only badge beside the actions.
   */
  itemVariant?: 'compact' | 'detailed';
  /** `after`-mode only: initial category resolved per file before upload starts. */
  resolveDefaultCategory?: (file: File) => string;
  /**
   * When categories are present: gate upload on a picked category (`before`) or tag afterwards
   * (`after`, default). `before` is a HARD gate — until a type is picked the add-files control is
   * inert (no file dialog, no drop) and states why, so a file can never be taken in untyped.
   */
  tagTiming?: 'after' | 'before';
  /**
   * The "add files" affordance's visual shape: a big drag-and-drop area (`dropzone`, default) or a
   * compact single row with a title/description on the left and an explicit upload button on the right
   * (`row` — no drag-and-drop handling, click-to-browse only). The rest of the component (validation,
   * batch upload, file list, progress/retry) is identical either way.
   *
   * `inline` is `row` with the before-mode type picker moved INTO the row, sharing it with the upload
   * button, instead of sitting in a block above the list.
   */
  zoneVariant?: 'dropzone' | 'inline' | 'row';
}

/** Summary returned by {@link FileUploadHandle.flush}. */
export interface FileUploadFlushResult {
  /** The source files whose deferred upload failed (their cards stay in an error/retry state). */
  failed: File[];
  /** How many staged files uploaded successfully. */
  uploaded: number;
}

/** Imperative handle exposed via `ref` — lets a parent flush the files staged in `deferred` mode. */
export interface FileUploadHandle {
  /**
   * Uploads every not-yet-done item (still `staged`, or `error` from a previous attempt) through the batch
   * engine (`onUploadBatch`); never rejects. Re-running it after a partial failure retries the failed items,
   * so a parent can treat `flush()` itself as a retry path (not only the per-card retry button).
   */
  flush(): Promise<FileUploadFlushResult>;
}

// ─── Internal item model ─────────────────────────────────────────────────────

type UploadStatus = 'done' | 'error' | 'staged' | 'uploading';

interface InternalItem {
  /** Resolved/selected category. */
  category?: string;
  /** MIME type. */
  contentType: string;
  /** The source `File` (present for local uploads; absent for seeded items). */
  file?: File;
  /** Display name. */
  fileName: string;
  /** Persisted id (set once uploaded or when seeded). */
  id?: string;
  /** Stable client-side key (unique for the life of the list). */
  key: string;
  /** Object URL for an image thumbnail (owned locally → revoked on remove/unmount). */
  previewUrl?: string;
  /** Upload progress 0–100. */
  progress: number;
  /** Whether `previewUrl` was created by this component (must be revoked). */
  revocable: boolean;
  /** Size in bytes. */
  sizeBytes: number;
  /** UI status. */
  status: UploadStatus;
  /** Error label when `status === 'error'`. */
  error?: string;
  /** Carried from a seeded item for the detailed card's meta line. */
  uploadedAt?: Date | string;
  /** Carried from a seeded item for the detailed card's meta line. */
  uploadedBy?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

let keyCounter = 0;
function nextKey(): string {
  keyCounter += 1;
  return `fu-${keyCounter}`;
}

/**
 * Returns `true` when the file satisfies the `accept` string.
 * `accept` may mix extensions (`.pdf`) and MIME types (`image/png`, `image/*`),
 * matched case-insensitively. An empty/undefined `accept` accepts everything.
 */
function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;

  const tokens = accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();

  return tokens.some((token) => {
    if (token.startsWith('.')) return fileName.endsWith(token);
    if (token.endsWith('/*')) return mimeType.startsWith(token.slice(0, -1));
    return mimeType === token;
  });
}

function isImageFile(contentType: string, fileName: string): boolean {
  if (contentType.toLowerCase().startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(fileName);
}

function isPdfFile(contentType: string, fileName: string): boolean {
  return contentType.toLowerCase() === 'application/pdf' || /\.pdf$/i.test(fileName);
}

function fileExtension(fileName: string): string {
  const match = /\.([^.]+)$/.exec(fileName);
  return match ? match[1].toUpperCase() : '?';
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/** Formats a byte count into a compact human-readable string (locale-independent digits). */
function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const value = bytes / 1024 ** exponent;
  const rounded = exponent === 0 ? value : Math.round(value * 10) / 10;
  return `${rounded} ${BYTE_UNITS[exponent]}`;
}

/** The file's extension in caps (`contract-2026.pdf` -> `PDF`), or nothing when the name carries none. */
function formatExtension(fileName: string): string | undefined {
  const dot = fileName.lastIndexOf('.');
  const extension = dot > 0 ? fileName.slice(dot + 1) : '';
  return extension === '' ? undefined : extension.toUpperCase();
}

/**
 * The detailed card's second line: extension, size, uploader and upload date, joined by a middle dot.
 * Each part is dropped when the item does not carry it, so a record with no uploader reads
 * "PDF · 240 KB · 20 Feb 2026" rather than leaving a gap or an empty separator.
 */
function formatItemMeta(
  item: Pick<FileUploadItem, 'fileName' | 'sizeBytes' | 'uploadedAt' | 'uploadedBy'>,
  labels: FileUploadLabels,
): string {
  const uploadedAt = item.uploadedAt !== undefined ? labels.uploadedAtFormat?.(item.uploadedAt) : undefined;
  return [formatExtension(item.fileName), formatBytes(item.sizeBytes), item.uploadedBy, uploadedAt]
    .filter((part): part is string => part !== undefined && part !== '')
    .join(' · ');
}

/** Creates an object URL for image files when the browser supports it. */
function createPreviewUrl(file: File): string | undefined {
  if (!isImageFile(file.type, file.name)) return undefined;
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return undefined;
  return URL.createObjectURL(file);
}

function revokePreviewUrl(item: InternalItem): void {
  if (item.revocable && item.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(item.previewUrl);
  }
}

function toPublicItem(item: InternalItem): FileUploadItem {
  return {
    category: item.category,
    contentType: item.contentType,
    file: item.file,
    fileName: item.fileName,
    id: item.id ?? '',
    previewUrl: item.previewUrl,
    sizeBytes: item.sizeBytes,
  };
}

function seedItem(file: FileUploadItem): InternalItem {
  return {
    category: file.category,
    contentType: file.contentType,
    fileName: file.fileName,
    id: file.id,
    key: nextKey(),
    previewUrl: file.previewUrl,
    progress: 100,
    revocable: false,
    uploadedAt: file.uploadedAt,
    uploadedBy: file.uploadedBy,
    sizeBytes: file.sizeBytes,
    status: 'done',
  };
}

interface ValidationContext {
  accept?: string;
  category?: string;
  labels: FileUploadLabels;
  maxFiles: number;
  maxSizeBytes?: number;
  requiresCategory: boolean;
  shownCount: number;
}

/**
 * How many of `maxFiles` the list currently uses. In replace mode the uploaded files are about to be replaced,
 * so only the files still in flight (or errored) count; a drop of more files than that is still capped.
 */
function occupiedSlots(items: InternalItem[], replaceOnUpload: boolean | undefined): number {
  return replaceOnUpload ? items.filter((item) => item.status !== 'done').length : items.length;
}

/** Validates a candidate file; returns the matching error label or `null` when valid. */
function getValidationError(file: File, ctx: ValidationContext): string | null {
  if (ctx.requiresCategory && !ctx.category) return ctx.labels.errorNoType;
  if (ctx.shownCount >= ctx.maxFiles) return ctx.labels.errorTooMany;
  if (ctx.maxSizeBytes !== undefined && file.size > ctx.maxSizeBytes) return ctx.labels.errorTooLarge;
  if (!matchesAccept(file, ctx.accept)) return ctx.labels.errorBadType;
  return null;
}

/**
 * Builds a fresh internal item for a newly-added file. A valid file starts in `validStatus`
 * (`uploading` for immediate mode, `staged` for deferred mode); an invalid one starts in `error`.
 */
function buildItem(
  file: File,
  category: string | undefined,
  validationError: string | null,
  validStatus: 'staged' | 'uploading',
): InternalItem {
  const previewUrl = createPreviewUrl(file);
  return {
    category,
    contentType: file.type,
    error: validationError ?? undefined,
    file,
    fileName: file.name,
    key: nextKey(),
    previewUrl,
    progress: 0,
    revocable: previewUrl !== undefined,
    sizeBytes: file.size,
    status: validationError ? 'error' : validStatus,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface CategoryPickerProps {
  categories: FileUploadCategoryOption[];
  className?: string;
  disabled: boolean;
  onChange: (value: string) => void;
  placeholder: string;
  triggerLabel: string;
  value?: string;
}

function CategorySelect({
  categories,
  className,
  disabled,
  onChange,
  placeholder,
  triggerLabel,
  value,
}: CategoryPickerProps): React.JSX.Element {
  return (
    <Select disabled={disabled} onValueChange={onChange} value={value ?? ''}>
      <SelectTrigger aria-label={triggerLabel} className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {categories.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface DropZoneProps {
  accept?: string;
  /** Before-mode with no type picked yet: the zone takes no file until one is. */
  awaitingCategory: boolean;
  /** Id of the copy explaining why the zone is inert, when it is. */
  describedBy?: string;
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  isFull: boolean;
  labels: FileUploadLabels;
  onFiles: (files: File[]) => void;
}

function DropZone({
  accept,
  awaitingCategory,
  describedBy,
  disabled,
  inputRef,
  isFull,
  labels,
  onFiles,
}: DropZoneProps): React.JSX.Element {
  const [isDragActive, setIsDragActive] = React.useState(false);
  const isBlocked = disabled || isFull || awaitingCategory;

  function handleDrop(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragActive(false);
    if (isBlocked) return;
    onFiles(Array.from(event.dataTransfer.files ?? []));
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    if (!isBlocked) setIsDragActive(true);
  }

  function handleDragLeave(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragActive(false);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    onFiles(Array.from(event.target.files ?? []));
    // Reset so selecting the same file again re-triggers change.
    event.target.value = '';
  }

  function openFileDialog(): void {
    if (!isBlocked) inputRef.current?.click();
  }

  return (
    <div
      aria-describedby={describedBy}
      aria-disabled={isBlocked}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors',
        isDragActive ? 'border-primary bg-accent/40' : 'border-input',
        isBlocked && 'cursor-not-allowed opacity-60',
      )}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Upload aria-hidden className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{labels.dropzone}</p>
      <Button disabled={isBlocked} onClick={openFileDialog} size="sm" type="button" variant="outline">
        {labels.browse}
      </Button>
      <input
        accept={accept}
        className="hidden"
        disabled={isBlocked}
        multiple
        onChange={handleInputChange}
        ref={inputRef}
        type="file"
      />
    </div>
  );
}

interface AddDocumentRowProps {
  accept?: string;
  /** Before-mode with no type picked yet: the row's upload button takes no file until one is. */
  awaitingCategory: boolean;
  /** Id of the copy explaining why the row is inert, when it is. */
  describedBy?: string;
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  isFull: boolean;
  labels: FileUploadLabels;
  /** Rendered at the start of the row, before the title block — the `inline` variant's type picker. */
  leading?: React.ReactNode;
  onFiles: (files: File[]) => void;
}

/** The type picker's accessible name: its visible label when there is one, else its placeholder. */
function pickerName(labels: FileUploadLabels): string {
  return labels.typeLabel ?? labels.typePlaceholder;
}

/**
 * The file list's header: a title on the left, a count on the right. Each half renders only when its
 * label is supplied, and the row disappears when neither is — so a consumer whose own section header
 * already carries this information is not forced to repeat it inside the uploader.
 */
function FileListHeader({
  count,
  labels,
  maxFiles,
}: {
  count: number;
  labels: FileUploadLabels;
  maxFiles: number;
}): React.JSX.Element | null {
  if (labels.listTitle === undefined && labels.countOf === undefined) {
    return null;
  }

  return (
    <div className="flex items-center justify-between">
      {labels.listTitle !== undefined && <span className="text-sm font-medium">{labels.listTitle}</span>}
      {labels.countOf !== undefined && (
        <span className="text-xs text-muted-foreground tabular-nums">{labels.countOf(count, maxFiles)}</span>
      )}
    </div>
  );
}

/**
 * The before-mode type picker, with its visible label rendered only when one is supplied. `inline` is
 * the shape used inside the add row: it takes the row's free width instead of stacking a labelled block.
 */
function TypePickerField({
  categories,
  disabled,
  inline = false,
  labels,
  onChange,
  value,
}: {
  categories: FileUploadCategoryOption[];
  disabled: boolean;
  inline?: boolean;
  labels: FileUploadLabels;
  onChange: (value: string) => void;
  value: string | undefined;
}): React.JSX.Element {
  const picker = (
    <CategorySelect
      categories={categories}
      disabled={disabled}
      onChange={onChange}
      placeholder={labels.typePlaceholder}
      triggerLabel={pickerName(labels)}
      value={value}
    />
  );

  if (inline) {
    return <div className="min-w-48 flex-1">{picker}</div>;
  }

  return (
    <div className="space-y-1.5">
      {labels.typeLabel !== undefined && <span className="text-sm font-medium">{labels.typeLabel}</span>}
      {picker}
    </div>
  );
}

/**
 * The before-mode type picker, rendered at exactly one of two placements: `row` puts it inside the add
 * row (the `inline` variant), `above` stacks it over the list (every other variant). Each placement
 * renders only when it is the one in effect, so the caller places both and never chooses.
 */
function BeforeTypePicker({
  categories,
  disabled,
  labels,
  onChange,
  placement,
  requiresCategory,
  value,
  zoneVariant,
}: {
  categories: FileUploadCategoryOption[] | undefined;
  disabled: boolean;
  labels: FileUploadLabels;
  onChange: (value: string) => void;
  placement: 'above' | 'row';
  requiresCategory: boolean;
  value: string | undefined;
  zoneVariant: 'dropzone' | 'inline' | 'row';
}): React.JSX.Element | null {
  const inline = zoneVariant === 'inline';
  if (!requiresCategory || categories === undefined || inline !== (placement === 'row')) {
    return null;
  }

  return (
    <TypePickerField
      categories={categories}
      disabled={disabled}
      inline={inline}
      labels={labels}
      onChange={onChange}
      value={value}
    />
  );
}

interface CategoryGate {
  /** The add-files control takes no file while this is true. */
  awaitingCategory: boolean;
  /** `aria-describedby` target for the copy saying why — undefined once the gate is open. */
  describedBy?: string;
}

/**
 * Before-mode gate: a file taken in with no type has nowhere to be filed, so the add-files control
 * never opens until one is picked. Refusing the file dialog outright beats accepting the file and
 * then rejecting it — the user is stopped before they choose, not after.
 */
function resolveCategoryGate(requiresCategory: boolean, picked: string | undefined, hintId: string): CategoryGate {
  const awaitingCategory = requiresCategory && !picked;
  return { awaitingCategory, describedBy: awaitingCategory ? hintId : undefined };
}

/**
 * The copy under a shut gate. Muted, not destructive: nothing has gone wrong yet — this is the control
 * saying what it is waiting for. The same string doubles as the error, but only on a file that somehow
 * reaches the list untyped.
 */
function AwaitingTypeHint({
  id,
  reason,
  shown,
}: {
  id: string;
  reason: string;
  shown: boolean;
}): React.JSX.Element | null {
  if (!shown) return null;

  return (
    <p className="text-xs text-muted-foreground" id={id}>
      {reason}
    </p>
  );
}

/**
 * The `zoneVariant: 'row'` add-files affordance: a single row (title + muted description on the left, an
 * explicit upload button on the right) instead of a big drag-and-drop area. Click-to-browse only — no
 * drag/drop handling — since the row is a compact list-item-style control, not a large drop target.
 */
function AddDocumentRow({
  accept,
  awaitingCategory,
  describedBy,
  disabled,
  inputRef,
  isFull,
  labels,
  leading,
  onFiles,
}: AddDocumentRowProps): React.JSX.Element {
  const isBlocked = disabled || isFull || awaitingCategory;

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    onFiles(Array.from(event.target.files ?? []));
    // Reset so selecting the same file again re-triggers change.
    event.target.value = '';
  }

  function openFileDialog(): void {
    if (!isBlocked) inputRef.current?.click();
  }

  return (
    <div
      aria-describedby={describedBy}
      aria-disabled={isBlocked}
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3.5',
        // Not `isBlocked`: the inline variant's type picker lives in this row, and dimming the one
        // control that lifts the gate would read as "nothing here works".
        (disabled || isFull) && 'opacity-60',
      )}
    >
      {leading}
      <div className="mr-auto flex flex-col">
        {labels.addRowTitle !== undefined && <span className="text-sm font-semibold">{labels.addRowTitle}</span>}
        {labels.addRowDescription && <span className="text-xs text-muted-foreground">{labels.addRowDescription}</span>}
      </div>
      <Button className="shrink-0 gap-1.5" disabled={isBlocked} onClick={openFileDialog} size="sm" type="button">
        <Upload aria-hidden className="h-3.5 w-3.5" />
        {labels.addRowButton ?? labels.browse}
      </Button>
      <input
        accept={accept}
        className="hidden"
        disabled={isBlocked}
        multiple
        onChange={handleInputChange}
        ref={inputRef}
        type="file"
      />
    </div>
  );
}

interface ThumbnailProps {
  contentType: string;
  fileName: string;
  previewUrl?: string;
}

function Thumbnail({ contentType, fileName, previewUrl }: ThumbnailProps): React.JSX.Element {
  const base = 'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted';

  if (previewUrl) {
    return (
      <div className={base}>
        <img alt={fileName} className="h-full w-full object-contain" src={previewUrl} />
      </div>
    );
  }

  if (isPdfFile(contentType, fileName)) {
    return (
      <div className={base}>
        <FileText aria-hidden className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={base}>
      <span className="text-[10px] font-semibold text-muted-foreground">{fileExtension(fileName)}</span>
    </div>
  );
}

interface StatusChipProps {
  item: InternalItem;
  labels: FileUploadLabels;
  reducedMotion: boolean;
}

function StatusChip({ item, labels, reducedMotion }: StatusChipProps): React.JSX.Element | null {
  // A staged (deferred) item is not in flight yet: no progress bar / done-check / error chip. Show only
  // the optional "will upload" hint when the consumer provides the label.
  if (item.status === 'staged') {
    return labels.staged ? (
      <span className="flex shrink-0 items-center text-xs text-muted-foreground">{labels.staged}</span>
    ) : null;
  }

  if (item.status === 'uploading') {
    return (
      <div className="flex shrink-0 items-center gap-2">
        <Progress
          aria-label={labels.uploading}
          className={cn('h-1.5 w-24', reducedMotion && '[&>*]:transition-none')}
          value={item.progress}
        />
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{Math.round(item.progress)}%</span>
      </div>
    );
  }

  if (item.status === 'done') {
    return (
      <span className="flex shrink-0 items-center gap-1 text-xs text-primary">
        <CheckCircle2 aria-hidden className="h-3.5 w-3.5" />
      </span>
    );
  }

  return (
    <span className="flex shrink-0 items-center gap-1 text-xs text-destructive" role="alert">
      <AlertCircle aria-hidden className="h-3.5 w-3.5" />
      {item.error ?? labels.errorUpload}
    </span>
  );
}

/**
 * A card's name/meta block. `detailed` stacks the meta line under the name and folds the size into it;
 * `compact` keeps one line with the size on the right.
 */
function FileCardBody({
  item,
  itemVariant,
  labels,
  reducedMotion,
}: {
  item: InternalItem;
  itemVariant: 'compact' | 'detailed';
  labels: FileUploadLabels;
  reducedMotion: boolean;
}): React.JSX.Element {
  const thumbnail = <Thumbnail contentType={item.contentType} fileName={item.fileName} previewUrl={item.previewUrl} />;
  const status = <StatusChip item={item} labels={labels} reducedMotion={reducedMotion} />;

  if (itemVariant === 'detailed') {
    return (
      <>
        {thumbnail}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium" title={item.fileName}>
            {item.fileName}
          </span>
          <span className="truncate text-xs text-muted-foreground">{formatItemMeta(item, labels)}</span>
        </span>
        {status}
      </>
    );
  }

  return (
    <>
      {thumbnail}
      <span className="min-w-0 flex-1 truncate text-sm font-medium" title={item.fileName}>
        {item.fileName}
      </span>
      {status}
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatBytes(item.sizeBytes)}</span>
    </>
  );
}

/**
 * The item's type as a read-only badge — what a `detailed` card shows in place of the per-card picker,
 * for a before-mode uploader where the type was chosen at upload and is not re-pickable afterwards.
 */
function CategoryBadge({
  categories,
  itemVariant,
  showPerCardPicker,
  value,
}: {
  categories: FileUploadCategoryOption[] | undefined;
  itemVariant: 'compact' | 'detailed';
  showPerCardPicker: boolean;
  value: string | undefined;
}): React.JSX.Element | null {
  const label = categories?.find((option) => option.value === value)?.label;
  if (itemVariant !== 'detailed' || showPerCardPicker || label === undefined) {
    return null;
  }

  return (
    <Badge className="shrink-0 font-normal" variant="secondary">
      {label}
    </Badge>
  );
}

/**
 * The per-card remove control — entirely ABSENT (not merely disabled) on a read-only list: a
 * greyed-out button would still be a control on screen, and AC4 requires none where the list can't
 * be managed.
 */
function RemoveButton({
  disabled,
  item,
  label,
  onRemove,
}: {
  disabled: boolean;
  item: InternalItem;
  label: string;
  onRemove: (item: InternalItem) => void;
}): React.JSX.Element | null {
  if (disabled) return null;

  return (
    <Button
      aria-label={label}
      className="shrink-0 text-muted-foreground hover:text-destructive"
      onClick={(): void => onRemove(item)}
      size="icon"
      type="button"
      variant="ghost"
    >
      <X />
    </Button>
  );
}

interface FileCardProps {
  categories?: FileUploadCategoryOption[];
  disabled: boolean;
  item: InternalItem;
  labels: FileUploadLabels;
  onDownload?: (item: InternalItem) => void;
  onOpen?: (item: InternalItem) => void;
  onRemove: (item: InternalItem) => void;
  onRetag: (item: InternalItem, value: string) => void;
  onRetry: (item: InternalItem) => void;
  itemVariant: 'compact' | 'detailed';
  reducedMotion: boolean;
  showPerCardPicker: boolean;
}

function FileCard({
  categories,
  disabled,
  item,
  itemVariant,
  labels,
  onDownload,
  onOpen,
  onRemove,
  onRetag,
  onRetry,
  reducedMotion,
  showPerCardPicker,
}: FileCardProps): React.JSX.Element {
  // Only a persisted, done item is openable — uploading/error items expose no open affordance.
  const isOpenable = onOpen !== undefined && item.status === 'done';
  const openLabel = `${labels.open} ${item.fileName}`;
  const fileMeta = <FileCardBody item={item} itemVariant={itemVariant} labels={labels} reducedMotion={reducedMotion} />;

  return (
    <li className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-card-foreground shadow-sm">
      {/* Thumbnail + name + status + size — one centered row; the whole block opens the doc when done. */}
      {isOpenable ? (
        <button
          aria-label={openLabel}
          className="-mx-1.5 -my-1 flex min-w-0 flex-1 items-center gap-3 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
          onClick={(): void => onOpen?.(item)}
          type="button"
        >
          {fileMeta}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">{fileMeta}</div>
      )}

      <CategoryBadge
        categories={categories}
        itemVariant={itemVariant}
        showPerCardPicker={showPerCardPicker}
        value={item.category}
      />

      {/* Category picker (Acquisition) stays OUTSIDE the open target so it never triggers open. */}
      {showPerCardPicker && item.status === 'done' && categories && (
        <CategorySelect
          categories={categories}
          className="h-7 w-40 shrink-0 text-xs"
          disabled={disabled}
          onChange={(value): void => onRetag(item, value)}
          placeholder={labels.typePlaceholder}
          triggerLabel={pickerName(labels)}
          value={item.category}
        />
      )}

      {item.status === 'error' && (
        <Button
          aria-label={labels.retry}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          disabled={disabled}
          onClick={(): void => onRetry(item)}
          size="icon"
          type="button"
          variant="ghost"
        >
          <RotateCcw />
        </Button>
      )}

      {/* View (open) — an explicit affordance next to download so non-technical users see the file can be
          opened/previewed (the whole row is also clickable). Same target as the row's open action.
          Not gated by `disabled`: it is a READ, and SWOOP-183 US9 keeps every file readable/downloadable
          even on an archived (read-only) vehicle. Also keeps parity with the row's own open button
          above, which has never been disabled — don't reintroduce `disabled={disabled}` here. */}
      {isOpenable && (
        <Button
          aria-label={openLabel}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          onClick={(): void => onOpen?.(item)}
          size="icon"
          type="button"
          variant="ghost"
        >
          <Eye />
        </Button>
      )}

      {/* Download (save-to-disk) — a sibling next to remove, OUTSIDE the open target so it never triggers
          open. Only for a persisted, done item and only when the consumer wires `onDownload`.
          Not gated by `disabled`: it is a READ, and SWOOP-183 US9 keeps every file downloadable even on
          an archived (read-only) vehicle — don't reintroduce `disabled={disabled}` here. */}
      {onDownload && item.status === 'done' && (
        <Button
          // Names the file, like the open action, so several Download buttons in one list are distinguishable.
          aria-label={`${labels.download} ${item.fileName}`}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          onClick={(): void => onDownload(item)}
          size="icon"
          type="button"
          variant="ghost"
        >
          <Download />
        </Button>
      )}

      <RemoveButton disabled={disabled} item={item} label={labels.remove} onRemove={onRemove} />
    </li>
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/** Tracks the user's reduced-motion preference (SSR-safe, listens for changes). */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent): void => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return (): void => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

// ─── Component ─────────────────────────────────────────────────────────────

interface TaggingMode {
  requiresCategoryBeforeUpload: boolean;
  showPerCardPicker: boolean;
}

/** Derives the tagging behaviour from the category-related props. */
function resolveTaggingMode(
  categories: FileUploadCategoryOption[] | undefined,
  fixedCategory: string | undefined,
  tagTiming: 'after' | 'before' | undefined,
): TaggingMode {
  const hasPicker = Array.isArray(categories) && categories.length > 0 && !fixedCategory;
  const timing = tagTiming ?? 'after';
  return {
    requiresCategoryBeforeUpload: hasPicker && timing === 'before',
    showPerCardPicker: hasPicker && timing === 'after',
  };
}

/**
 * Orders the add-files control against the file list. The `row` variant (a fixed-category upload row,
 * e.g. insurance documents) shows what's already uploaded first, then the add-row below it; the default
 * `dropzone` variant keeps the drop target above the list.
 */
function renderZoneSections(
  zoneVariant: 'dropzone' | 'inline' | 'row',
  addControl: React.ReactNode,
  filesList: React.ReactNode,
): React.ReactNode {
  return zoneVariant !== 'dropzone' ? (
    <>
      {filesList}
      {addControl}
    </>
  ) : (
    <>
      {addControl}
      {filesList}
    </>
  );
}

/**
 * The component body: in read-only mode just the file list (no type picker, no add affordance); otherwise the
 * before-mode type picker over the ordered add-control + list. Kept out of {@link FileUpload} so its branch
 * does not count against that component's complexity budget.
 */
function renderUploadBody(
  readOnly: boolean,
  typePicker: React.ReactNode,
  zoneVariant: 'dropzone' | 'inline' | 'row',
  addControl: React.ReactNode,
  filesList: React.ReactNode,
): React.ReactNode {
  if (readOnly) {
    return filesList;
  }
  return (
    <>
      {typePicker}
      {renderZoneSections(zoneVariant, addControl, filesList)}
    </>
  );
}

export const FileUpload = React.forwardRef<FileUploadHandle, FileUploadProps>(function FileUpload(
  {
    accept,
    categories,
    disabled = false,
    fixedCategory,
    initialFiles,
    itemVariant = 'compact',
    labels,
    maxFiles = 1,
    maxSizeBytes,
    mode = 'immediate',
    onDownload,
    onItemCountChange,
    onOpen,
    onRemove,
    onRetag,
    onUploadBatch,
    readOnly = false,
    replaceOnUpload,
    resolveDefaultCategory,
    tagTiming,
    zoneVariant = 'dropzone',
  },
  ref,
): React.JSX.Element {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const awaitingTypeHintId = React.useId();
  const reducedMotion = usePrefersReducedMotion();

  const { requiresCategoryBeforeUpload, showPerCardPicker } = resolveTaggingMode(categories, fixedCategory, tagTiming);

  const [items, setItems] = React.useState<InternalItem[]>(() => (initialFiles ?? []).map(seedItem));
  const [pickedCategory, setPickedCategory] = React.useState<string | undefined>(undefined);
  const [zoneError, setZoneError] = React.useState<string | null>(null);

  // Stable refs shared with async upload callbacks and cleanup.
  const controllersRef = React.useRef<Map<string, AbortController>>(new Map());
  const removedIdsRef = React.useRef<Set<string>>(new Set());
  const itemsRef = React.useRef<InternalItem[]>(items);
  itemsRef.current = items;

  // ── Latest-prop ref so the upload closure never goes stale ──────────────
  const onUploadBatchRef = React.useRef(onUploadBatch);
  onUploadBatchRef.current = onUploadBatch;

  const patchItem = React.useCallback((key: string, patch: Partial<InternalItem>): void => {
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  }, []);

  /**
   * Replace mode: once a new upload succeeds, the files it replaced (every other uploaded item) leave the
   * list. No `onRemove` call — the consumer's upload replaced them server-side — and their ids are remembered
   * so a stale `initialFiles` cannot bring them back.
   */
  const dropReplacedItems = React.useCallback((keepKeys: string[]): void => {
    const keep = new Set(keepKeys);
    setItems((current) =>
      current.filter((item) => {
        if (keep.has(item.key) || item.status !== 'done') return true;
        if (item.id) removedIdsRef.current.add(item.id);
        revokePreviewUrl(item);
        return false;
      }),
    );
  }, []);

  /**
   * Uploads a whole batch through the consumer's `onUploadBatch` in one shot (one shared setup for N
   * files). All items share a single AbortController (removing any of them aborts the batch). Per-file
   * progress is reported via `ctx.onFileProgress`. Resolves (never rejects) with a {@link FileUploadFlushResult}
   * summary of the batch: each returned file is marked done and counted in `uploaded`; any file the consumer
   * did not return (or a rejected batch) is marked errored and pushed to `failed`. The immediate path ignores
   * the summary (fire-and-forget); `flush` awaits it.
   */
  const runUploadBatch = React.useCallback(
    (
      targets: InternalItem[],
      upload: NonNullable<FileUploadProps['onUploadBatch']>,
    ): Promise<FileUploadFlushResult> => {
      const pairs = targets.flatMap((item) => (item.file ? [{ file: item.file, item }] : []));
      if (pairs.length === 0) return Promise.resolve({ failed: [], uploaded: 0 });

      const controller = new AbortController();
      const byFile = new Map<File, InternalItem>();
      for (const { file, item } of pairs) {
        controllersRef.current.set(item.key, controller);
        byFile.set(file, item);
        patchItem(item.key, { error: undefined, progress: 0, status: 'uploading' });
      }

      // `ctx.category` describes the whole batch, so it is only meaningful when every file shares one
      // category (fixed / before-mode picked). In after-mode a per-file `resolveDefaultCategory` can tag
      // files differently; exposing the first file's category as batch-wide would mis-tag the rest, so we
      // leave it undefined unless the batch is uniform.
      const batchCategories = new Set(pairs.map(({ item }) => item.category));
      const context: FileUploadBatchContext = {
        category: batchCategories.size === 1 ? pairs[0].item.category : undefined,
        onFileProgress: (file, percent): void => {
          const item = byFile.get(file);
          if (item) patchItem(item.key, { progress: Math.max(0, Math.min(100, percent)) });
        },
        signal: controller.signal,
      };
      const clearControllers = (): void => {
        for (const { item } of pairs) controllersRef.current.delete(item.key);
      };
      // Removing any item aborts the shared controller, which typically rejects the whole batch with no
      // results. Surface any items still uploading as errored (retryable) instead of leaving them stuck
      // with a spinner forever. The removed item is already gone from state and any already-done item
      // keeps its state. (The resolve path honours partial results directly, so it does not use this.)
      const failStalledOnAbort = (): void => {
        const keys = new Set(pairs.map(({ item }) => item.key));
        setItems((current) =>
          current.map((item) =>
            keys.has(item.key) && item.status === 'uploading'
              ? { ...item, error: labels.errorUpload, status: 'error' }
              : item,
          ),
        );
      };

      return upload(
        pairs.map(({ file }) => file),
        context,
      ).then(
        (results) => {
          clearControllers();
          // Honour whatever the consumer persisted even if the batch was aborted mid-flight: a file whose
          // upload+record finished before the abort comes back with its id and is marked done, so a later
          // retry can't orphan or duplicate that server record. Files with no result (removed, or their
          // upload aborted) fall through to the errored pass below. patchItem on an already-removed item
          // is a harmless no-op.
          const done = new Set<File>();
          for (const result of results) {
            const item = byFile.get(result.file);
            if (item) {
              patchItem(item.key, {
                category: result.category ?? item.category,
                id: result.id,
                progress: 100,
                status: 'done',
              });
              done.add(result.file);
            }
          }
          if (replaceOnUpload === true && done.size > 0) dropReplacedItems(pairs.map(({ item }) => item.key));
          // Any file the consumer did not return is a failure — mark it errored (retryable) and report it
          // so `flush` can surface the failed files to its caller.
          const failed: File[] = [];
          for (const { file, item } of pairs) {
            if (!done.has(file)) {
              patchItem(item.key, { error: labels.errorUpload, status: 'error' });
              failed.push(file);
            }
          }
          return { failed, uploaded: done.size };
        },
        (reason: unknown) => {
          clearControllers();
          if (controller.signal.aborted) {
            failStalledOnAbort();
            return { failed: [], uploaded: 0 };
          }
          const message = reason instanceof Error && reason.message ? reason.message : labels.errorUpload;
          for (const { item } of pairs) patchItem(item.key, { error: message, status: 'error' });
          return { failed: pairs.map(({ file }) => file), uploaded: 0 };
        },
      );
    },
    [dropReplacedItems, labels.errorUpload, patchItem, replaceOnUpload],
  );

  // Deferred mode only: upload every not-yet-done item — freshly `staged`, or `error` from an earlier
  // attempt — through the SAME batch engine in one shot, so re-running `flush()` retries the failed items
  // (a parent can treat `flush()` itself as a retry path). Derives its summary from the resolved batch
  // result; never rejects. Resolves to an empty summary when nothing is pending or no handler is wired.
  const flush = React.useCallback((): Promise<FileUploadFlushResult> => {
    const batch = onUploadBatchRef.current;
    const pending = itemsRef.current.filter((item) => item.status === 'staged' || item.status === 'error');
    if (!batch || pending.length === 0) return Promise.resolve({ failed: [], uploaded: 0 });
    return runUploadBatch(pending, batch);
  }, [runUploadBatch]);

  React.useImperativeHandle(ref, () => ({ flush }), [flush]);

  /** Resolves the category stamped on a newly-added file for the current tagging mode. */
  const resolveCategory = React.useCallback(
    (file: File): string | undefined => {
      if (fixedCategory) return fixedCategory;
      if (requiresCategoryBeforeUpload) return pickedCategory;
      if (showPerCardPicker) return resolveDefaultCategory?.(file) ?? categories?.[0]?.value;
      return undefined;
    },
    [
      categories,
      fixedCategory,
      pickedCategory,
      requiresCategoryBeforeUpload,
      resolveDefaultCategory,
      showPerCardPicker,
    ],
  );

  const addFiles = React.useCallback(
    (files: File[]): void => {
      if (disabled || files.length === 0) return;
      setZoneError(null);

      const gateCategory = fixedCategory ?? (requiresCategoryBeforeUpload ? pickedCategory : undefined);

      // Deferred mode stages valid files locally (no network) until `flush`; immediate mode uploads now.
      const validStatus = mode === 'deferred' ? 'staged' : 'uploading';
      let projectedShown = occupiedSlots(itemsRef.current, replaceOnUpload);
      const newItems = files.map((file) => {
        const validationError = getValidationError(file, {
          accept,
          category: gateCategory,
          labels,
          maxFiles,
          maxSizeBytes,
          requiresCategory: requiresCategoryBeforeUpload,
          shownCount: projectedShown,
        });
        projectedShown += 1;
        return buildItem(file, resolveCategory(file), validationError, validStatus);
      });

      setItems((current) => [...current, ...newItems]);
      // Immediate mode marks valid files `uploading` and hands the whole drop to `onUploadBatch` as ONE
      // batch now; deferred mode leaves them `staged` (nothing uploading) until an imperative `flush()`.
      // With no handler wired, files just stay listed.
      const uploading = newItems.filter((item) => item.status === 'uploading');
      const batch = onUploadBatchRef.current;
      if (batch && uploading.length > 0) void runUploadBatch(uploading, batch);
    },
    [
      accept,
      disabled,
      fixedCategory,
      labels,
      maxFiles,
      maxSizeBytes,
      mode,
      pickedCategory,
      replaceOnUpload,
      requiresCategoryBeforeUpload,
      resolveCategory,
      runUploadBatch,
    ],
  );

  const handleRemove = React.useCallback(
    (target: InternalItem): void => {
      // Aborting an in-flight upload is always safe to do eagerly.
      const controller = controllersRef.current.get(target.key);
      if (controller) {
        controller.abort();
        controllersRef.current.delete(target.key);
      }

      const id = target.id;
      // A not-yet-persisted local item has nothing to delete server-side → drop it immediately.
      if (!id || !onRemove) {
        revokePreviewUrl(target);
        setItems((current) => current.filter((item) => item.key !== target.key));
        return;
      }

      // Persisted item: defer the optimistic removal until the delete resolves, so a rejected
      // mutation leaves the card visible in an error state instead of silently hiding the doc.
      Promise.resolve(onRemove(toPublicItem(target))).then(
        (outcome) => {
          // An explicit `false` is the consumer declining (a confirmation the user dismissed), which
          // is not a failure: leave the card untouched rather than erroring or hiding it.
          if (outcome === false) {
            return;
          }
          revokePreviewUrl(target);
          removedIdsRef.current.add(id);
          setItems((current) => current.filter((item) => item.key !== target.key));
        },
        (reason: unknown) => {
          const message = reason instanceof Error && reason.message ? reason.message : labels.errorUpload;
          patchItem(target.key, { error: message, status: 'error' });
        },
      );
    },
    [labels.errorUpload, onRemove, patchItem],
  );

  const handleRetry = React.useCallback(
    (target: InternalItem): void => {
      const validationError = target.file
        ? getValidationError(target.file, {
            accept,
            category: target.category,
            labels,
            maxFiles,
            maxSizeBytes,
            requiresCategory: requiresCategoryBeforeUpload,
            // Validate against the OTHER items. In replace mode an uploaded file is about to be replaced, so it
            // does not count (`occupiedSlots`): a failed replacement can be retried while the old file shows.
            shownCount: occupiedSlots(
              itemsRef.current.filter((item) => item.key !== target.key),
              replaceOnUpload,
            ),
          })
        : labels.errorNoFile;

      if (validationError) {
        patchItem(target.key, { error: validationError, status: 'error' });
        return;
      }
      const batch = onUploadBatchRef.current;
      if (batch) void runUploadBatch([target], batch);
    },
    [accept, labels, maxFiles, maxSizeBytes, patchItem, replaceOnUpload, requiresCategoryBeforeUpload, runUploadBatch],
  );

  const handleRetag = React.useCallback(
    (target: InternalItem, value: string): void => {
      // Apply the new category optimistically for responsiveness, remembering the old one to revert to.
      const previousCategory = target.category;
      patchItem(target.key, { category: value });
      if (!onRetag) return;

      // Defer trusting the change: a rejected retag rolls the category back and surfaces the error
      // instead of leaving the wrong category on screen.
      Promise.resolve(onRetag({ ...toPublicItem(target), category: value }, value)).catch((reason: unknown) => {
        const message = reason instanceof Error && reason.message ? reason.message : labels.errorUpload;
        patchItem(target.key, { category: previousCategory, error: message, status: 'error' });
      });
    },
    [labels.errorUpload, onRetag, patchItem],
  );

  const handleOpen = React.useCallback(
    (target: InternalItem): void => {
      if (onOpen) onOpen(toPublicItem(target));
    },
    [onOpen],
  );

  const handleDownload = React.useCallback(
    (target: InternalItem): void => {
      if (onDownload) onDownload(toPublicItem(target));
    },
    [onDownload],
  );

  // ── Reconcile `initialFiles` by id: add newly-persisted files, and fill in a missing `previewUrl`
  //    on an already-seeded persisted item (e.g. an async read-SAS thumbnail URL arrives after mount).
  //    Add-only for new items; fill-in-only for existing ones — never clobbers a live object-URL preview
  //    from a session upload. Returns the same array reference when nothing changed (no re-render/loop). ──
  React.useEffect(() => {
    if (!initialFiles || initialFiles.length === 0) return;
    const byId = new Map<string, FileUploadItem>();
    for (const file of initialFiles) byId.set(file.id, file);
    setItems((current) => {
      const presentIds = new Set(current.map((item) => item.id).filter((id): id is string => Boolean(id)));
      const additions = initialFiles
        .filter((file) => !presentIds.has(file.id) && !removedIdsRef.current.has(file.id))
        .map(seedItem);
      let changed = additions.length > 0;
      const merged = current.map((item) => {
        if (item.id !== undefined && item.previewUrl === undefined) {
          const source = byId.get(item.id);
          if (source?.previewUrl !== undefined) {
            changed = true;
            return { ...item, previewUrl: source.previewUrl };
          }
        }
        return item;
      });
      return changed ? [...merged, ...additions] : current;
    });
  }, [initialFiles]);

  // ── Abort in-flight uploads and revoke object URLs on unmount ──────────────
  React.useEffect(() => {
    const controllers = controllersRef.current;
    return (): void => {
      for (const controller of controllers.values()) controller.abort();
      controllers.clear();
      for (const item of itemsRef.current) revokePreviewUrl(item);
    };
  }, []);

  const isFull = occupiedSlots(items, replaceOnUpload) >= maxFiles;
  const gate = resolveCategoryGate(requiresCategoryBeforeUpload, pickedCategory, awaitingTypeHintId);

  // The add-files control (row or dropzone) plus its validation error, grouped so the pair moves together
  // when reordered against the file list below.
  function handlePickCategory(value: string): void {
    setPickedCategory(value);
    setZoneError(null);
  }

  const pickerProps = {
    categories,
    disabled,
    labels,
    onChange: handlePickCategory,
    requiresCategory: requiresCategoryBeforeUpload,
    value: pickedCategory,
    zoneVariant,
  };

  const addControl = (
    <>
      {zoneVariant !== 'dropzone' ? (
        <AddDocumentRow
          {...gate}
          accept={accept}
          disabled={disabled}
          inputRef={inputRef}
          isFull={isFull}
          labels={labels}
          leading={<BeforeTypePicker {...pickerProps} placement="row" />}
          onFiles={addFiles}
        />
      ) : (
        <DropZone
          {...gate}
          accept={accept}
          disabled={disabled}
          inputRef={inputRef}
          isFull={isFull}
          labels={labels}
          onFiles={addFiles}
        />
      )}

      <AwaitingTypeHint id={awaitingTypeHintId} reason={labels.errorNoType} shown={gate.awaitingCategory} />

      {zoneError && (
        <p className="text-sm text-destructive" role="alert">
          {zoneError}
        </p>
      )}
    </>
  );

  React.useEffect(() => {
    onItemCountChange?.(items.length);
  }, [items.length, onItemCountChange]);

  const filesList = (
    <div className="space-y-2">
      <FileListHeader count={items.length} labels={labels} maxFiles={maxFiles} />

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{labels.empty}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <FileCard
              categories={categories}
              disabled={disabled || readOnly}
              item={item}
              key={item.key}
              labels={labels}
              onDownload={onDownload ? handleDownload : undefined}
              onOpen={onOpen ? handleOpen : undefined}
              onRemove={handleRemove}
              onRetag={handleRetag}
              itemVariant={itemVariant}
              onRetry={handleRetry}
              reducedMotion={reducedMotion}
              showPerCardPicker={showPerCardPicker}
            />
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {renderUploadBody(
        readOnly,
        <BeforeTypePicker {...pickerProps} placement="above" />,
        zoneVariant,
        addControl,
        filesList,
      )}
    </div>
  );
});

FileUpload.displayName = 'FileUpload';
